// MatchStore (002 store rework, step 2) — the single, render-decoupled source of truth
// for the game-relevant match state. Timestamp-based throughout (no per-field refs, no
// drift), with commands as the SOLE writer composing the already-tested engine functions.
// Display code only reads (getState/subscribe) and derives times via the selectors.
//
// MatchState = {
//   clock: { period, durationMs, startedAt|null },         // one running clock
//   home / away: TeamState,
//   timeout: { team, durationMs, startedAt } | null,
//   break:   { durationMs, startedAt } | null,
// }
// TeamState = { name, score, fouls, hasTimeout, timeoutCount,
//               penalties: [ {id, playerNumber, durationMs, startedAt|null} ],  // parallel
//               inferiority: [ {durationMs, startedAt|null} ] }                 // sequential queue

import { createStore } from './createStore.js';
import { createClock, startClock, stopClock, remainingMs } from '../engine/clock.js';
import { nextPeriod } from '../engine/period.js';
import * as pen from '../engine/penalties.js';
import * as inf from '../engine/inferiority.js';

const TEAMS = ['home', 'away'];

/** The board's brand colour before an edition configures one (the web edition's long-standing blue). */
export const DEFAULT_BOARD_COLOR = '#1a374d';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

// The fields a coded goal celebration (011/012) is drawn from — nothing else crosses into the overlay.
function animationOverlay({ text, backgroundColor, textColor, playerNumber, playerName, runId }) {
  return { kind: 'animation', text, backgroundColor, textColor, playerNumber, playerName, runId };
}

function createTeam(name) {
  // `logo` (base64 data-URL or url, null when ad-hoc/absent) is an optional presentation slot
  // that auto-syncs with the rest of the team state to the secondary window (US6 / FR-019).
  return { name, logo: null, score: 0, fouls: 0, hasTimeout: false, timeoutCount: 0, penalties: [], inferiority: [] };
}

export function createInitialMatchState({ periodDurationMs = 25 * 60 * 1000, homeName = 'Home', awayName = 'Away' } = {}) {
  return {
    // `hasStarted` becomes true on the first start() and stays true until reset/advancePeriod —
    // it distinguishes "match never started" (all inferiority segments editable 1:1) from
    // "started, now paused" (only the front segment is editable). FR-035 / use-case D6.
    clock: { period: 1, hasStarted: false, ...createClock(periodDurationMs) },
    home: createTeam(homeName),
    away: createTeam(awayName),
    timeout: null,
    break: null,
    // Presentation-only state that must DISPLAY + SYNC to the audience window. The active
    // league's sponsor logo is merged into the shared carousel (FR-020). `videoOverlay` is the
    // active fullscreen goal video/animation (US5 / FR-015..017): when non-null it takes over the
    // audience window; cleared (null) → the live scoreboard returns. `carouselImages` /
    // `breakOverlayImages` are the operator's configured sponsor images, mirrored here from the
    // desktop settings so the read-only audience window renders them too (they live outside the
    // pure store otherwise). It all auto-syncs like the rest of MatchState.
    // `masterVolume` (0..1) is the operator's general output level, mirrored for the audience window's
    // overlay video audio (the control window's own music/buzzer read it straight from the catalog).
    // `boardColor` (the club's brand colour behind the bands) and `showTeamInferiority` (whether the
    // audience board shows the team-penalty bar) are operator settings for the same reason: the
    // audience webview cannot read the control window's settings, so they travel as match state.
    presentation: {
      leagueSponsorLogo: null,
      videoOverlay: null,
      carouselImages: [],
      breakOverlayImages: [],
      masterVolume: 1,
      boardColor: DEFAULT_BOARD_COLOR,
      showTeamInferiority: true,
    },
  };
}

const startTeamClocks = (t, now) => ({ ...t, penalties: pen.startAll(t.penalties, now), inferiority: inf.startFront(t.inferiority, now) });
const stopTeamClocks = (t, now) => ({ ...t, penalties: pen.pauseAll(t.penalties, now), inferiority: inf.pauseFront(t.inferiority, now) });

export function createMatchStore(initialState = createInitialMatchState(), options = {}) {
  const store = createStore(initialState, options);
  const get = store.getState;
  const set = store.setState;
  const mut = (team, fn) => set((s) => ({ ...s, [team]: fn(s[team]) }));

  // A period change resets both teams' timeouts (2 per half): clear the counters, the pending flags,
  // and any running timeout. Returned as a partial-state patch to merge into a set().
  const resetTimeoutsPatch = (s) => ({
    timeout: null,
    home: { ...s.home, hasTimeout: false, timeoutCount: 0 },
    away: { ...s.away, hasTimeout: false, timeoutCount: 0 },
  });

  const isRunning = () => get().clock.startedAt !== null;

  // Align `now` to the running clock's whole-second grid: a penalty/segment started at
  // this instant flips its displayed second in lockstep with the game clock (STAGE 3 desync
  // fix). With elapsed < 1s the display still shows the full duration (ceil), so remaining is
  // unaffected; only the sub-second phase is snapped. While paused, returns `now` unchanged.
  const gridSnap = (now) => {
    const { startedAt } = get().clock;
    if (startedAt === null) return now;
    return startedAt + Math.floor((now - startedAt) / 1000) * 1000;
  };

  return {
    getState: get,
    subscribe: store.subscribe,

    // ---- clock ----
    start(now) {
      set((s) => ({ ...s, clock: { ...startClock(s.clock, now), hasStarted: true }, home: startTeamClocks(s.home, now), away: startTeamClocks(s.away, now) }));
    },
    stop(now) {
      set((s) => ({ ...s, clock: stopClock(s.clock, now), home: stopTeamClocks(s.home, now), away: stopTeamClocks(s.away, now) }));
    },
    setClockRemaining(durationMs) {
      // Editing the clock is only meaningful while paused; freeze the new remaining.
      set((s) => ({ ...s, clock: { ...s.clock, durationMs: Math.max(0, durationMs), startedAt: null } }));
    },
    setPeriod(period) {
      // A period change (each period = a half) resets both teams' timeouts — 2 per half.
      set((s) => ({ ...s, clock: { ...s.clock, period }, ...resetTimeoutsPatch(s) }));
    },
    /** Clear the started flag (web RESET path, which sets period+remaining directly). */
    clearStarted() {
      set((s) => ({ ...s, clock: { ...s.clock, hasStarted: false } }));
    },
    advancePeriod({ regularPeriods = 2, maxPeriods = 4, periodDurationMs, overtimeDurationMs } = {}) {
      const s = get();
      const res = nextPeriod({ period: s.clock.period, homeScore: s.home.score, awayScore: s.away.score, regularPeriods, maxPeriods });
      if (res.advanced) {
        const dur = res.period <= regularPeriods ? periodDurationMs : overtimeDurationMs;
        set((st) => ({
          ...st,
          clock: { period: res.period, durationMs: dur ?? st.clock.durationMs, startedAt: null },
          ...resetTimeoutsPatch(st), // a new period resets both teams' timeouts (2 per half)
        }));
      }
      return res;
    },
    clockRemainingMs(now) {
      return remainingMs(get().clock, now);
    },

    // ---- teams ----
    setName(team, name) { mut(team, (t) => ({ ...t, name })); },
    // Set a team's identity in one shot when a stored team is picked (name + logo). Ad-hoc entry
    // passes { name, logo: null }. Logo defaults to null when omitted (US6 / FR-019, FR-022).
    setTeam(team, { name, logo = null } = {}) { mut(team, (t) => ({ ...t, name, logo })); },
    setLeagueSponsorLogo(url) {
      set((s) => ({ ...s, presentation: { ...s.presentation, leagueSponsorLogo: url ?? null } }));
    },
    // Mirror the operator's configured sponsor images into the synced presentation state so the
    // read-only audience window shows them (the images themselves live in the desktop settings).
    // Reference-equal writes are skipped so a repeatedly-called sync driver doesn't broadcast (and
    // overwrite the secondary's state) every tick.
    setCarouselImages(images) {
      set((s) => {
        const next = images ?? [];
        if (next === s.presentation.carouselImages) return s;
        return { ...s, presentation: { ...s.presentation, carouselImages: next } };
      });
    },
    setBreakOverlayImages(images) {
      set((s) => {
        const next = images ?? [];
        if (next === s.presentation.breakOverlayImages) return s;
        return { ...s, presentation: { ...s.presentation, breakOverlayImages: next } };
      });
    },
    // The club's brand colour behind the scoreboard bands, mirrored for the audience window like the
    // sponsor images. A malformed value keeps the current colour rather than blanking the board —
    // this is the surface the whole audience is looking at.
    setBoardColor(color) {
      set((s) => {
        const next = typeof color === 'string' && HEX_COLOR.test(color.trim()) ? color.trim() : null;
        if (next === null || next === s.presentation.boardColor) return s;
        return { ...s, presentation: { ...s.presentation, boardColor: next } };
      });
    },
    // Whether the AUDIENCE board shows the team-penalty bar. Some clubs want the penalties listed
    // without it; the operator's own view keeps it either way, since that is where it is managed.
    setShowTeamInferiority(show) {
      set((s) => {
        const next = !!show;
        if (next === s.presentation.showTeamInferiority) return s;
        return { ...s, presentation: { ...s.presentation, showTeamInferiority: next } };
      });
    },
    // General output volume (0..1), mirrored here so the AUDIENCE window — a separate webview that
    // cannot read the control window's settings — plays overlay video audio at the operator's level.
    // Unchanged writes are skipped so a per-render sync driver never broadcasts.
    setMasterVolume(volume) {
      set((s) => {
        const next = Math.min(1, Math.max(0, typeof volume === 'number' ? volume : 1));
        if (next === s.presentation.masterVolume) return s;
        return { ...s, presentation: { ...s.presentation, masterVolume: next } };
      });
    },

    // ---- fullscreen video / animation overlay (US5 goal videos + US4 pre-game videos) ----
    // The overlay = { src, kind:'video'|'image', muted:boolean } | null. When non-null it takes over
    // the audience window; cleared (null) → the live scoreboard returns. `muted` is honored by the
    // audience overlay element: US5 goal videos play MUTED (goal-category music supplies their audio),
    // while US4 pre-game videos play WITH audio (muted:false).
    //
    // Set the active fullscreen goal overlay: a player's own goal media, or — feature 011 — the CODED
    // celebration, which carries no media at all but the parameters it is drawn from
    // ({ kind:'animation', text, backgroundColor, textColor, playerNumber, runId }). Goal videos are ALWAYS muted
    // (FR-016 clarification). Anything without a src that is not an animation is a no-op.
    playGoalOverlay(media) {
      if (!media) return;
      // US008 FR-028: tag the overlay source so the audience end-signal + operator overlay can tell a
      // goal-celebration video from a pre-game clip (goal interrupts a running sequence, then resumes).
      if (media.kind === 'animation') {
        // playerNumber/playerName are what the celebration REVEALS — already resolved by the control
        // window (feature 012): the operator's on/off switches never cross to the audience side, only
        // their result does. An empty string means "reveal nothing for this one".
        set((s) => ({ ...s, presentation: { ...s.presentation, videoOverlay: { ...animationOverlay(media), source: 'goal' } } }));
        return;
      }
      if (!media.src) return;
      const kind = media.kind === 'image' ? 'image' : 'video';
      const overlay = { src: media.src, kind, muted: true, source: 'goal' };
      // Feature 015: the control may attach the coded celebration to play once the clip has ended, so
      // the hall learns who scored. Kept only as a plain animation payload — never media, never a
      // follow-up of its own — so a follow-up can never chain.
      if (media.followUp?.kind === 'animation') overlay.followUp = animationOverlay(media.followUp);
      set((s) => ({ ...s, presentation: { ...s.presentation, videoOverlay: overlay } }));
    },
    /**
     * Feature 015: a goal clip has ended — play its follow-up animation if it has one, else clear. Both
     * windows run this (the audience locally in the frame the clip ends, the control authoritatively), and
     * the payload is identical on both, so the swap neither flashes the scoreboard nor remounts.
     */
    advanceGoalOverlay() {
      set((s) => {
        const followUp = s.presentation.videoOverlay?.followUp;
        return {
          ...s,
          presentation: { ...s.presentation, videoOverlay: followUp ? { ...followUp, source: 'goal' } : null },
        };
      });
    },
    /**
     * Generic fullscreen overlay setter (US4 pre-game sequence + single library video). The caller
     * chooses `muted` (pre-game videos pass muted:false → audio on). `kind` is 'video' | 'image'
     * (defaults to 'video'). Passing a falsy media/src is a no-op.
     */
    playVideoOverlay(media) {
      if (!media || !media.src) return;
      const kind = media.kind === 'image' ? 'image' : 'video';
      const muted = !!media.muted;
      // US2: per-video loop. A loop clip repeats and holds (the audience renders <video loop>, which
      // fires no 'ended' → no auto-advance). Goal overlays use playGoalOverlay and never loop.
      const loop = !!media.loop;
      // US008 FR-028: pre-game overlays are tagged source:'pregame' (vs goal videos' 'goal').
      set((s) => ({ ...s, presentation: { ...s.presentation, videoOverlay: { src: media.src, kind, muted, loop, source: 'pregame' } } }));
    },
    /** Clear the active overlay → the live scoreboard returns on the audience window. */
    clearGoalOverlay() {
      set((s) => ({ ...s, presentation: { ...s.presentation, videoOverlay: null } }));
    },
    setScore(team, delta) { mut(team, (t) => ({ ...t, score: Math.max(0, t.score + delta) })); },
    setFouls(team, delta) { mut(team, (t) => ({ ...t, fouls: Math.max(0, t.fouls + delta) })); },

    // ---- penalties (parallel) + team-penalty ("inferiority") coupling ----
    //
    // A team's TEAM PENALTY is the sequential sum of its penalties. It normally starts at the second
    // penalty — one penalty alone is not shown. The exception (operator request): while the OTHER
    // team is already serving a team penalty, a lone penalty on this side is shown too. Otherwise the
    // board would credit one team with a running team penalty and show nothing for the other, even
    // though both have a player off — which reads as an error from the stands.
    //
    // The rule is applied in both directions, so the order the penalties arrive in does not matter:
    // a team that gains a team penalty pulls the opponent's lone penalty into one as well. Once
    // started, a team penalty runs its own length out — it does not disappear when the opponent's
    // ends.
    addPenalty(team, { id, durationMs, playerNumber = null }, now) {
      const other = team === 'home' ? 'away' : 'home';
      set((s) => {
        const t = s[team];
        if (t.penalties.length >= pen.MAX_PENALTIES_PER_TEAM) return s;
        const running = isRunning();
        // Snap a started-while-running penalty (and its team-penalty front segment) to the
        // clock's second grid so they flip in lockstep with the game clock (STAGE 3 desync fix).
        const startAt = running ? gridSnap(now) : null;
        const anchor = startAt ?? now;
        const p = { id, playerNumber, durationMs, startedAt: startAt };
        const penalties = [...t.penalties, p];
        let inferiority = t.inferiority;
        if (inferiority.length > 0) {
          // Team penalty already active → append this penalty as a queued segment.
          inferiority = inf.appendSegment(inferiority, durationMs);
        } else if (penalties.length >= 2) {
          // Reaching two penalties triggers it: front = first penalty's remaining, then this one.
          // Read that remaining at the SAME instant the segment starts (the grid-snapped one, not the
          // raw `now`) — otherwise the segment is short by the snap distance (up to 1 s) and drains
          // out of phase with the penalty it mirrors.
          inferiority = [
            { durationMs: remainingMs(t.penalties[0], anchor), startedAt: startAt },
            { durationMs, startedAt: null },
          ];
        } else if (s[other].inferiority.length > 0) {
          // The opponent is already down: this lone penalty is shown as a team penalty too.
          inferiority = [{ durationMs, startedAt: startAt }];
        }

        const next = { ...s, [team]: { ...t, penalties, inferiority } };

        // Mirror the rule the other way: if THIS team has just gained a team penalty, an opponent
        // sitting on a single penalty gets one as well (they cannot have had two — that would have
        // given them one already).
        const o = next[other];
        if (inferiority.length > 0 && o.inferiority.length === 0 && o.penalties.length > 0) {
          next[other] = {
            ...o,
            inferiority: [{ durationMs: remainingMs(o.penalties[0], anchor), startedAt: startAt }],
          };
        }
        return next;
      });
    },
    setPenaltyPlayerNumber(team, id, playerNumber) {
      mut(team, (t) => ({
        ...t,
        penalties: t.penalties.map((p) => (p.id === id ? { ...p, playerNumber } : p)),
      }));
    },
    removePenalty(team, id, now) {
      mut(team, (t) => {
        const index = t.penalties.findIndex((p) => p.id === id);
        if (index === -1) return t;
        const penalties = t.penalties.filter((p) => p.id !== id);
        // Remove the matching inferiority segment: front removal promotes the next (FR-036). The
        // promoted segment starts on the clock's second grid so it flips with the game clock.
        const running = isRunning();
        const inferiority = index === 0 ? inf.removeFront(t.inferiority, gridSnap(now), running) : inf.removeAt(t.inferiority, index);
        return { ...t, penalties, inferiority };
      });
    },
    editPenaltyTime(team, id, newDurationMs, now) {
      mut(team, (t) => {
        const index = t.penalties.findIndex((p) => p.id === id);
        if (index === -1) return t;
        const old = t.penalties[index];
        const deltaMs = newDurationMs - old.durationMs;
        const penalties = t.penalties.map((p) => (p.id === id ? { ...p, durationMs: newDurationMs } : p));
        // Before the match has EVER started each penalty maps 1:1 to its inferiority segment by
        // position, so editing penalty `index` shifts segment `index` (initial-setup edits). Once
        // started (even if now paused), only an edit to the active (front) penalty adjusts
        // inferiority; queued (non-front) edits are ignored (FR-035 / use-case D6).
        const started = get().clock.hasStarted;
        const inferiority = !started
          ? inf.editAt(t.inferiority, index, deltaMs)
          : index === 0
            ? inf.editFront(t.inferiority, deltaMs)
            : t.inferiority;
        return { ...t, penalties, inferiority };
      });
    },
    // Both of the following are called on EVERY tick by the edition's clock driver, so they must
    // return the state UNCHANGED (same reference) when nothing expired — createStore skips the
    // notify + broadcast then, and the tick stays free of pointless re-renders and window traffic.
    clearExpiredPenalties(now) {
      set((s) => {
        let changed = false;
        const next = { ...s };
        for (const team of TEAMS) {
          const penalties = pen.clearExpired(s[team].penalties, now);
          if (penalties.length === s[team].penalties.length) continue;
          next[team] = { ...s[team], penalties };
          changed = true;
        }
        return changed ? next : s;
      });
    },
    /** Drain finished inferiority front segments (call on tick while running). */
    advanceInferiority(now) {
      set((s) => {
        let changed = false;
        const next = { ...s };
        for (const team of TEAMS) {
          const inferiority = inf.advance(s[team].inferiority, now);
          if (inferiority === s[team].inferiority) continue; // advance() returns the same ref when idle
          next[team] = { ...s[team], inferiority };
          changed = true;
        }
        return changed ? next : s;
      });
    },
    penaltyRemainingMs(team, id, now) {
      const p = get()[team].penalties.find((x) => x.id === id);
      return p ? remainingMs(p, now) : 0;
    },
    inferiorityMs(team, now) {
      return inf.totalInferiorityMs(get()[team].inferiority, now);
    },

    // ---- timeout ----
    // Two-step UI flow: requestTimeout flags the team (hasTimeout) WITHOUT starting the
    // countdown or counting it; startTimeout then begins the fixed countdown and increments
    // the per-team counter. End/cancel/expiry → endTimeout clears both.
    requestTimeout(team) {
      mut(team, (t) => ({ ...t, hasTimeout: true }));
    },
    startTimeout(team, durationMs, now) {
      set((s) => ({ ...s, timeout: { team, durationMs, startedAt: now }, [team]: { ...s[team], hasTimeout: true, timeoutCount: s[team].timeoutCount + 1 } }));
    },
    endTimeout() {
      set((s) => {
        const team = s.timeout?.team;
        const base = { ...s, timeout: null, home: { ...s.home, hasTimeout: false }, away: { ...s.away, hasTimeout: false } };
        return base;
      });
    },
    timeoutRemainingMs(now) {
      const to = get().timeout;
      return to ? remainingMs(to, now) : 0;
    },
    /** Per-period reset of both teams' timeout counters (E3 / Timeout 5). */
    resetTimeoutCounts() {
      set((s) => ({ ...s, home: { ...s.home, timeoutCount: 0 }, away: { ...s.away, timeoutCount: 0 } }));
    },
    /** Give a used timeout back to a team — clicking one of its red timeout dots (min 0). */
    decreaseTimeout(team) {
      mut(team, (t) => ({ ...t, timeoutCount: Math.max(0, t.timeoutCount - 1) }));
    },
    /** Apply a synced timeout snapshot (secondary windows mirror the main window). */
    applyTimeout(snapshot) {
      // snapshot = { timeout: {team,durationMs,startedAt}|null, home:{hasTimeout,timeoutCount}, away:{...} }
      set((s) => ({
        ...s,
        timeout: snapshot.timeout ?? null,
        home: { ...s.home, hasTimeout: snapshot.home?.hasTimeout ?? false, timeoutCount: snapshot.home?.timeoutCount ?? s.home.timeoutCount },
        away: { ...s.away, hasTimeout: snapshot.away?.hasTimeout ?? false, timeoutCount: snapshot.away?.timeoutCount ?? s.away.timeoutCount },
      }));
    },

    // ---- break ----
    startBreak(durationMs, now) { set((s) => ({ ...s, break: { durationMs, startedAt: now } })); },
    endBreak() { set((s) => ({ ...s, break: null })); },
    breakRemainingMs(now) {
      const b = get().break;
      return b ? remainingMs(b, now) : 0;
    },
    /** Apply a synced break snapshot (secondary windows mirror the main window). */
    applyBreak(snapshot) {
      // snapshot = { durationMs, startedAt } | null
      set((s) => ({ ...s, break: snapshot ?? null }));
    },

    // ---- cross-window apply (secondary windows replace from a broadcast) ----
    replacePenalties(team, penalties) { mut(team, (t) => ({ ...t, penalties })); },
    replaceInferiority(team, inferiority) { mut(team, (t) => ({ ...t, inferiority })); },

    // ---- reset (keep team names + period duration; clear the rest) ----
    reset(periodDurationMs = get().clock.durationMs) {
      set((s) => {
        const fresh = createInitialMatchState({ periodDurationMs, homeName: s.home.name, awayName: s.away.name });
        // Keep everything that tracks the operator's SETTINGS rather than the match: the sponsor
        // images, the brand colour and the audience display choices. The league sponsor logo and any
        // active overlay are still cleared.
        return {
          ...fresh,
          presentation: {
            ...fresh.presentation,
            carouselImages: s.presentation?.carouselImages ?? [],
            breakOverlayImages: s.presentation?.breakOverlayImages ?? [],
            boardColor: s.presentation?.boardColor ?? DEFAULT_BOARD_COLOR,
            showTeamInferiority: s.presentation?.showTeamInferiority ?? true,
          },
        };
      });
    },
  };
}
